import { datadogLogs, type LogsEvent } from '@datadog/browser-logs';
import { datadogRum, type RumInitConfiguration } from '@datadog/browser-rum';
import type { AnalyticsProvider, LogProvider, ObservabilityProvider } from '../types';
import {
  scrubPhiObject,
  STANDARD_REDACT_PATTERN,
  safeRumAttributes,
  scrubUrlValues,
  type UrlTemplateSets,
  REDACTED_URL,
} from '../redact';
import { compileRouteTemplates } from '../route-template-match';
import { GALAPAGOS_API_ROUTE_TEMPLATES } from '../routes/galapagos-api-routes.generated';
import { templatizeRumViewName } from '../rum-view-name';
import { rumRelayProxy } from '../rum-intake/relay-proxy';
import { safeMetricTags } from '../metric-tags';
import { analyticsSubjectIdSchema } from '@galapagos/product-analytics';
import { isPresignedUrl } from './presigned-url';

// Re-exported for the web provider's existing test surface; the canonical
// scrubber now lives in ../redact (shared with @galapagos/telemetry).
export { scrubPhi } from '../redact';

/**
 * Web ObservabilityProvider — Datadog RUM + browser-logs, for the browser
 * surfaces (web + atlas).
 *
 * This module holds the ONLY static `@datadog/browser-*` imports in the package.
 * It is reached exclusively through the factory's dynamic `import('./providers/web')`,
 * so Node consumers that never select the web provider never pull the browser
 * bundles into their module graph.
 *
 * Interface mapping (ADR-068 best-effort-per-runtime):
 *   logs.log        -> datadogLogs.logger[level]
 *   analytics.track -> datadogRum.addAction            (custom RUM action)
 *   analytics.setUser -> datadogRum.setUser + datadogLogs.setUser, with the
 *                        raw canonical member id as usr.id (ADR-069)
 *   metrics.*       -> datadogRum.addAction / addTiming
 *   traces.span     -> RUM custom action marking the wrapped call
 * NOTE: on the browser, `metrics.*` and `traces.span` are NOT real Datadog
 * metrics or APM spans — real APM lives server-side (ADR-068) and stitches to a
 * RUM session via `allowedTracingUrls`. RUM auto-tracks views + click actions
 * natively (`trackUserInteractions`); the `analytics.track`/`metrics` mappings
 * are a secondary custom-signal path.
 */

const DEFAULT_SITE = 'datadoghq.com';
const DEFAULT_SAMPLE_RATE = 100;
// Percentage of RUM resources (fetch/XHR to a first-party origin) that generate a
// linked APM trace. Set explicitly rather than relying on the SDK default so the
// RUM→APM stitch rate is predictable; pairs with the APM retention filter (Datadog
// org config, out of repo scope) so a kept RUM session's linked trace is retained.
const RESOURCE_TRACE_SAMPLE_RATE = 100;
// Fallback service tag. Callers pass an explicit `service` (galapagos-web /
// galapagos-atlas); the fallback keeps a token-only atlas init tagged as before.
const DEFAULT_SERVICE = 'atlas';

type AllowedTracingUrl = NonNullable<RumInitConfiguration['allowedTracingUrls']>[number];

export interface WebProviderConfig {
  /** Datadog browser client token. When absent, both logs and RUM stay inert. */
  clientToken?: string;
  /** Datadog RUM application id. When absent, RUM stays off (logs may still init). */
  applicationId?: string;
  site?: string;
  /** Datadog environment (preview / development / …) for per-env isolation. */
  env: string;
  /** Service version, typically read from `package.json` at the call site. */
  version: string;
  sessionSampleRate?: number;
  service?: string;
  /** Backend origin(s) the client calls, for RUM->APM trace propagation (ADR-068). */
  allowedTracingUrls?: AllowedTracingUrl[];
  /**
   * RUM intake relay that strips CSS selector fields server-side before
   * forwarding to Datadog. Passed as the RUM SDK's `proxy` option only: log
   * events carry no selectors, so logs keep sending to Datadog directly.
   * Omitted → RUM sends to the Datadog intake directly, as before.
   *
   * The relay is told this provider's Datadog site (`dd-site`), so batches
   * reach the intake for the site the client token belongs to rather than a
   * site the relay assumes; the relay accepts only known Datadog sites.
   */
  rumProxyUrl?: string;
  /**
   * App-specific route templates, used to reduce URLs in telemetry to route
   * templates instead of interpolated paths (ADR-069 Rule 1).
   *
   * The Galapagos public API set is universal and always included; these are the
   * per-app additions, which cannot be baked into the package without
   * mis-matching another app's URLs:
   *
   * - `api` — non-Galapagos API routes this app calls (Atlas: backend2).
   * - `page` — this app's own page routes, for `view.url` / `view.referrer`.
   *
   * Omitting a set is safe but lossy: URLs in that class are destroyed rather
   * than templated. Every app that initialises this provider should supply its
   * page set, or it forfeits view-URL grouping.
   */
  routeTemplates?: {
    api?: readonly string[];
    page?: readonly string[];
  };
  /**
   * RUM view names (page paths) on which only the view and its errors are
   * sent: click and custom actions, network resources and long tasks are
   * dropped. For pages where what a person does can itself be health
   * information — how many answers they saved, or a control only some
   * conditions get — even though no single attribute names it. The SDK's view
   * counters count only events that pass `beforeSend`, so the view carries no
   * trace of them either. A trailing slash is ignored. Omitted → every event
   * is sent as before.
   */
  interactionWithheldViews?: readonly string[];
}

/**
 * `beforeSend` hook: scrub PHI from the event in place with the standard-mode
 * pattern (ADR-069 Rule 5) so no PHI egresses on logs OR RUM. Axios catch blocks
 * attach the upstream response body, which on a member-detail view contains PHI;
 * this strips it. Returns nothing so the (scrubbed) event is still sent.
 */
export function scrubLogEvent(event: LogsEvent, sets: UrlTemplateSets = {}): void {
  // The whole hook is guarded, not just the URL walk. `scrubPhi` runs first and
  // is not itself defensive — it cannot be, since it is shared byte-for-byte with
  // the mobile provider — so a hostile accessor made the hook throw into the
  // Datadog SDK, which both violates the never-throw requirement and can drop or
  // destabilise events. On any failure the event is blanked rather than passed
  // on: losing one event beats emitting an unsanitised one.
  try {
    scrubWebEvent(event, sets);
  } catch {
    blankEvent(event);
  }
}

function scrubWebEvent(event: object, sets: UrlTemplateSets): void {
  const scrubbed = scrubPhiObject(event, new WeakSet(), STANDARD_REDACT_PATTERN);
  // Key-based scrub first, so free-text slots (`message`, `body`, `stack`,
  // `details`) are already redacted and the URL walk never has to reason about
  // prose. Then the value-level pass, which is the only one that can reach an
  // identifier embedded inside a URL string.
  mergeSanitised(event, scrubUrlValues(scrubbed, sets));
}

/**
 * The subtrees a page has opted out of behavioural capture — the same selector
 * PostHog honours (`posthog.web.ts`), so one marker governs both vendors.
 */
const CAPTURE_SUPPRESSED_SELECTOR = '.ph-no-capture, [data-ph-no-capture]';

/**
 * Whether a RUM event is a click action on an element inside a
 * capture-suppressed subtree, which is dropped rather than sent.
 *
 * Dropping is the only lever that works here. The SDK runs `beforeSend` on a
 * clone and copies back only an allow-list of fields, so deleting the click's
 * CSS selector (built from the element's `data-testid`, classes and sibling
 * position — which can name the option a clinician picked) changes nothing
 * that is sent; returning `false` for a non-view event does discard it. The
 * DOM event that produced an automatic action arrives in the domain context.
 */
function isCaptureSuppressedAction(event: unknown, context: unknown): boolean {
  if (!event || typeof event !== 'object' || Reflect.get(event, 'type') !== 'action') return false;
  if (!context || typeof context !== 'object') return false;
  const events: unknown = Reflect.get(context, 'events');
  if (!Array.isArray(events) || typeof Element === 'undefined') return false;
  return events.some((domEvent: unknown) => {
    const target: unknown =
      domEvent && typeof domEvent === 'object' ? Reflect.get(domEvent, 'target') : null;
    return target instanceof Element && target.closest(CAPTURE_SUPPRESSED_SELECTOR) !== null;
  });
}

const INTERACTION_EVENT_TYPES: ReadonlySet<string> = new Set(['action', 'resource', 'long_task']);

function withoutTrailingSlash(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/** Whether a RUM event is an interaction on a view whose interactions are withheld. */
function isWithheldInteraction(event: unknown, withheldViews: ReadonlySet<string>): boolean {
  if (withheldViews.size === 0 || !event || typeof event !== 'object') return false;
  const type: unknown = Reflect.get(event, 'type');
  if (typeof type !== 'string' || !INTERACTION_EVENT_TYPES.has(type)) return false;
  const view: unknown = Reflect.get(event, 'view');
  const name: unknown = view && typeof view === 'object' ? Reflect.get(view, 'name') : undefined;
  return typeof name === 'string' && withheldViews.has(withoutTrailingSlash(name));
}

/**
 * Whether a RUM event is a resource fetch of a presigned object-storage link,
 * which is dropped rather than sent. A presigned URL is a bearer credential to
 * the object, and the URL sanitiser keeps an undeclared URL's origin — which,
 * for a virtual-hosted bucket, is the bucket's name. Nothing about such a
 * fetch is worth that: the media it loads is not an API call to trace.
 */
function isPresignedResource(event: unknown): boolean {
  if (!event || typeof event !== 'object' || Reflect.get(event, 'type') !== 'resource')
    return false;
  const resource: unknown = Reflect.get(event, 'resource');
  if (!resource || typeof resource !== 'object') return false;
  const url: unknown = Reflect.get(resource, 'url');
  return typeof url === 'string' && isPresignedUrl(url);
}

// RUM `beforeSend` — same two-pass treatment as the logs hook above. Both hooks
// must run the URL walk; sanitising only one would leave the other's events
// carrying interpolated URLs.
function scrubRumEvent(event: unknown, sets: UrlTemplateSets = {}): void {
  if (event && typeof event === 'object') {
    // Same guard as the logs hook — see `scrubLogEvent`.
    try {
      scrubWebEvent(event, sets);
    } catch {
      blankEvent(event);
    }
  }
}

/**
 * Best-effort blanking: remove every *configurable* own key, so a failed
 * sanitisation cannot emit the original.
 *
 * Deliberately not stated as unconditional. A non-configurable own property
 * cannot be deleted, so it would survive — but the SDK builds its events from
 * ordinary configurable data properties, and the reachable failure mode is a
 * throwing accessor on a *nested* app-supplied value, which leaves the top level
 * fully blankable. Claiming more than that would be a guarantee the code does
 * not keep.
 */
function blankEvent(event: object): void {
  for (const key of Object.keys(event)) {
    try {
      if (Reflect.deleteProperty(event, key)) continue;
      // Non-configurable: fall back to overwriting the value in place, which
      // still removes the payload even when the key itself cannot go.
      try {
        Reflect.set(event, key, REDACTED_URL);
      } catch {
        // Read-only as well — nothing further is possible for this key.
      }
    } catch {
      // A hostile Proxy may throw during deletion; leave the event as-is only
      // when its own access traps prevent the fail-closed fallback too.
      try {
        Reflect.set(event, key, REDACTED_URL);
      } catch {
        // Read-only as well — nothing further is possible for this key.
      }
    }
  }
}

/** Page templates from the most recent `createWebProvider`, for `startWebRumView`. */
let activePageTemplates: UrlTemplateSets['page'];

/**
 * Compile the template sets once per provider construction. Matching then
 * allocates no index and compiles no regex per event — the hooks run on every
 * event, on the browser main thread.
 */
export function compileUrlTemplateSets(
  routeTemplates?: WebProviderConfig['routeTemplates'],
): UrlTemplateSets {
  const origin = documentOrigin();
  return {
    api: compileRouteTemplates([...GALAPAGOS_API_ROUTE_TEMPLATES, ...(routeTemplates?.api ?? [])]),
    page: routeTemplates?.page ? compileRouteTemplates(routeTemplates.page) : undefined,
    // The page's own origin serves the bundle, so its asset paths are safe to
    // retain. Member-keyed media is served from a different origin and is
    // therefore destroyed. Derived rather than configured: no app has to
    // remember to declare it, and an unknown origin fails closed.
    assetOrigins: origin ? [origin] : undefined,
  };
}

/** The document origin, or `undefined` outside a browser. */
function documentOrigin(): string | undefined {
  try {
    return typeof window !== 'undefined' && window.location && window.location.origin
      ? window.location.origin
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Merge the sanitised tree back over the event.
 *
 * `Object.assign` only overwrites keys present in the source, so a sanitiser that
 * returned a non-object — the pathological-input path — would leave every
 * original field in place, fail-open at the very last step. In that case the
 * event is blanked instead: losing one event beats emitting an unsanitised one.
 */
function mergeSanitised(event: object, sanitised: unknown): void {
  if (isRecord(sanitised)) {
    Object.assign(event, sanitised);
    return;
  }
  for (const key of Object.keys(event)) Reflect.deleteProperty(event, key);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const webLogs: LogProvider = {
  log(level, message, structured, error) {
    const ddLogger = datadogLogs.logger;
    if (!ddLogger || typeof ddLogger[level] !== 'function') return;
    // Preserve a thrown Error in Datadog's dedicated `error` argument (stack +
    // grouping) rather than flattening it into the message context — parity with
    // the pre-provider Atlas façade. The Error may arrive explicitly (4th arg) or
    // as the whole structured value (single-arg `logger.error(msg, err)`).
    const err = error ?? (structured instanceof Error ? structured : undefined);
    const context = structured instanceof Error ? undefined : structured;
    ddLogger[level](message, context, err);
  },
};

/**
 * Build the web ObservabilityProvider. Initialises `@datadog/browser-logs` when
 * a client token is present, and `@datadog/browser-rum` additionally when a RUM
 * `applicationId` is also present. With no token (local dev) or no applicationId
 * (logs-only, e.g. atlas before its RUM app is minted) the corresponding sink
 * stays inert rather than crashing (ADR-068 Rule 6 fail-safe).
 */
export function createWebProvider(config: WebProviderConfig): ObservabilityProvider {
  const {
    clientToken,
    applicationId,
    site,
    env,
    version,
    sessionSampleRate,
    service = DEFAULT_SERVICE,
    allowedTracingUrls,
    rumProxyUrl,
    routeTemplates,
    interactionWithheldViews = [],
  } = config;
  const withheldViews: ReadonlySet<string> = new Set(
    interactionWithheldViews.map(withoutTrailingSlash),
  );

  const urlTemplateSets = compileUrlTemplateSets(routeTemplates);
  // `startWebRumView` is a module-level export with no handle on the provider,
  // so the page set is stashed here for it — same pattern as the SDK globals
  // this module already owns.
  activePageTemplates = urlTemplateSets.page;

  const rumEnabled = Boolean(clientToken && applicationId);

  if (clientToken) {
    datadogLogs.init({
      clientToken,
      site: site ?? DEFAULT_SITE,
      service,
      env,
      version,
      sessionSampleRate: sessionSampleRate ?? DEFAULT_SAMPLE_RATE,
      forwardErrorsToLogs: true,
      beforeSend: (event) => {
        scrubLogEvent(event, urlTemplateSets);
        return true;
      },
    });
  }

  if (clientToken && applicationId) {
    datadogRum.init({
      applicationId,
      clientToken,
      site: site ?? DEFAULT_SITE,
      service,
      env,
      version,
      sessionSampleRate: sessionSampleRate ?? DEFAULT_SAMPLE_RATE,
      // Explicit resource→trace sample rate (not the SDK default) for a predictable
      // RUM→APM stitch rate.
      traceSampleRate: RESOURCE_TRACE_SAMPLE_RATE,
      // Session Replay is OFF for v1 (ADR-069 Rule 6).
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      // Manual view tracking: the app's router integration calls `startWebRumView`
      // on each navigation so SPA route changes produce named RUM views.
      trackViewsManually: true,
      defaultPrivacyLevel: 'mask',
      ...(allowedTracingUrls ? { allowedTracingUrls } : {}),
      ...(rumProxyUrl ? { proxy: rumRelayProxy(rumProxyUrl, site ?? DEFAULT_SITE) } : {}),
      beforeSend: (event, context) => {
        if (isWithheldInteraction(event, withheldViews)) return false;
        if (isCaptureSuppressedAction(event, context)) return false;
        if (isPresignedResource(event)) return false;
        scrubRumEvent(event, urlTemplateSets);
        return true;
      },
    });
  }

  return {
    logs: webLogs,
    errors: {
      report: (name, attributes) => {
        if (rumEnabled) datadogRum.addError(new Error(name), { ...safeMetricTags(attributes) });
      },
    },
    metrics: {
      emit: (name, value, tags) => {
        if (rumEnabled) datadogRum.addAction(name, { value, ...safeMetricTags(tags) });
      },
      increment: (name, tags) => {
        if (rumEnabled) datadogRum.addAction(name, { ...safeMetricTags(tags) });
      },
      histogram: (name, value) => {
        if (rumEnabled) datadogRum.addTiming(name, value);
      },
    },
    traces: {
      span: async (name, attrs, fn) => {
        const finalAttrs = { ...attrs };
        const result = await fn({
          setAttributes: (attributes) => Object.assign(finalAttrs, attributes),
        });
        if (rumEnabled) {
          datadogRum.addAction(
            name,
            scrubPhiObject(finalAttrs, new WeakSet(), STANDARD_REDACT_PATTERN),
          );
        }
        return result;
      },
    },
    analytics: createWebRumAnalytics(config),
  };
}

/**
 * The RUM-backed analytics slot as a standalone concrete: `track` → a RUM custom
 * action; `setUser` → the already server-minted AnalyticsSubject on both RUM and
 * logs (ADR-069 Rule 3). Exported so an app can compose it with PostHog via `compositeAnalytics`
 * without re-initialising the SDKs (the RUM/logs globals are initialised once by
 * `createWebProvider`; this object is a thin wrapper over them). Gated on the same
 * tokens, so it is inert when RUM/logs are off (fail-safe).
 */
export function createWebRumAnalytics(config: WebProviderConfig): AnalyticsProvider {
  const rumEnabled = Boolean(config.clientToken && config.applicationId);
  const hasLogs = Boolean(config.clientToken);
  return {
    track: (event, properties) => {
      // Drop nested payloads + PHI-scrub scalars before the action reaches RUM
      // (ADR-069 no-free-text / safe-scalar contract) — the composite fans generic
      // product analytics (incl. nested params) into RUM.
      if (rumEnabled) datadogRum.addAction(event, safeRumAttributes(properties));
    },
    setUser: (userId) => {
      const subject = analyticsSubjectIdSchema.safeParse(userId);
      if (!subject.success) return;
      if (rumEnabled) datadogRum.setUser({ id: subject.data });
      if (hasLogs) datadogLogs.setUser({ id: subject.data });
    },
    clearUser: () => {
      if (rumEnabled) datadogRum.clearUser();
      if (hasLogs) datadogLogs.clearUser();
    },
  };
}

/**
 * Start a named RUM view (manual view tracking). The app's router integration
 * calls this on each route change so SPA navigations produce named RUM views.
 * No-op until `createWebProvider` has initialised RUM.
 */
export function startWebRumView(name?: string): void {
  datadogRum.startView(name ? templatizeRumViewName(name, activePageTemplates) : name);
}
